import { ArrowDown, LoaderCircle } from 'lucide-react';
import type { PullToRefresh } from '@/lib/pull-to-refresh';
import { cn } from '@/lib/utils';

/**
 * What the pull looks like: one 36px disc that comes out of the top edge, turns over as
 * it arms, and holds a spinner in a band of its own while the refresh runs.
 *
 * THE FINGER MOVES THE DISC AND THE RELEASE MOVES THE PAGE, which is the one thing in
 * here worth reading twice.
 *
 * While a finger is down the disc is an overlay: it tracks the touch exactly, costs no
 * layout, and passes over whatever is at the top of the screen for the half-second the
 * pull lasts. Pushing the content down per frame instead would mean either a transform
 * on the ancestor of every sticky header in the app — the letter's, the room's, the
 * profile's, each of which would get a new containing block — or a reflow of the whole
 * column on every touchmove.
 *
 * On release the content DOES move, once: `band` opens to `REST` under a 300ms
 * transition and the disc spins in the space that opens up. That is the state that
 * lasts long enough to be looked at, and an overlaid disc sitting on top of the feed's
 * search field for a second read as a collision rather than as a refresh. One height
 * transition per gesture is a price worth paying; sixty is not.
 *
 * FIRST IN THE SCROLLER AND STICKY AT ZERO HEIGHT, so the overlay needs no `relative`
 * on the container and takes part in no layout: `h-0` keeps it out of the flow and
 * `sticky top-0` pins it to the top edge of the scrollport rather than the top of the
 * content. What is above that edge is clipped by the scroller's own `overflow-y`, which
 * is what makes the disc look like it comes from off-screen.
 *
 * TWO GLYPHS, BECAUSE A ROTATING ARROW IS A DIFFERENT ICON HALFWAY THROUGH. The arrow
 * says which way to pull and flips to say the pull is enough; the spinner says the work
 * is running. Both versions of a single turning arrow were tried and both were worse:
 * one tied to the finger sits at 90° for most of the pull, which is the back button,
 * and one that keeps spinning through the refresh is a diagonal arrow in every frame
 * anybody actually looks at.
 *
 * THE SPIN AND THE TRAVEL ARE ON DIFFERENT ELEMENTS on purpose. A CSS animation on
 * `transform` replaces an inline `transform` outright, so a disc that both spun and
 * carried its own travel would snap to the top edge the moment the spin started.
 *
 * NOT GATED ON `prefers-reduced-motion`. Following a finger is Response motion, which
 * docs/1 § Motion exempts, and index.css already flattens the release transition for a
 * reader who asked for stillness — the disc then arrives and leaves without travelling,
 * which is the right reading of the preference rather than a gesture that stops working.
 */

/** How far above the top edge the disc waits, in CSS pixels: its own height and a
 *  little more, so nothing shows at rest. */
const HIDDEN = 40;

export function PullIndicator({
  travel,
  band,
  armed,
  refreshing,
  dragging,
}: PullToRefresh) {
  return (
    <>
      {/* One announcement, not a running commentary on the geometry. */}
      {refreshing && (
        <span className="sr-only" role="status">
          Refreshing
        </span>
      )}

      <div
        aria-hidden
        className="pointer-events-none sticky top-0 z-30 flex h-0 justify-center"
      >
        <div
          /* Tracks the finger exactly while it is down; eases only on release. */
          className={cn(!dragging && 'transition-transform duration-300')}
          style={{ transform: `translateY(${travel - HIDDEN}px)` }}
        >
          <span
            className={cn(
              'flex size-9 items-center justify-center rounded-full border border-border bg-background shadow-sm',
              refreshing && 'animate-spin',
            )}
          >
            {refreshing ? (
              <LoaderCircle className="size-4 text-foreground" strokeWidth={2.2} />
            ) : (
              <ArrowDown
                className={cn(
                  'size-4 transition-[rotate,color] duration-200',
                  armed ? 'text-foreground' : 'text-muted-foreground',
                )}
                strokeWidth={2.2}
                /* Two positions and a flip between them, NOT a rotation tied to how
                   far the finger has gone. Turning the arrow continuously parks it at
                   90° for most of the pull, where an arrow pointing sideways is not a
                   half-armed pull, it is the back button. */
                style={{ rotate: armed ? '180deg' : '0deg' }}
              />
            )}
          </span>
        </div>
      </div>

      {/* The band the disc spins in. Zero for the whole gesture, `REST` while the
          refresh runs, and it is what stops the disc landing on the row below. */}
      <div
        aria-hidden
        className="transition-[height] duration-300"
        style={{ height: band }}
      />
    </>
  );
}
